import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { McTopBar, McIcon } from "@maersk-global/mds-react-wrapper/components-core";
import ThemeToggle from "./ThemeToggle";
import FilterBar, { TREND_OPTIONS } from "./FilterBar";
import SectionNav from "./SectionNav";
import AtAGlanceSection from "./sections/AtAGlanceSection";
import AgingComparisonSection from "./sections/AgingComparisonSection";
import TimelinessComparisonSection from "./sections/TimelinessComparisonSection";
import CostCountSection from "./sections/CostCountSection";
import InvalidCreditNoteSection from "./sections/InvalidCreditNoteSection";
import RejectionSection from "./sections/RejectionSection";
import E2ETimelinessSection from "./sections/E2ETimelinessSection";
import MaterialSpendCostSpikeSection, { DEFAULT_SPIKE_FILTERS } from "./sections/MaterialSpendCostSpikeSection";
import CXTimelinessSection from "./sections/CXTimelinessSection";
import LastRefreshed from "../LastRefreshed";
import {
  getPendingApprovalAging,
  getPendingPoAging,
  getApprovalTimeliness,
  getPoTimeliness,
  getInvoiceTimeliness,
  getAwaitingTimeliness,
} from "../../services/metricsApi";
import "./Analytics.css";

const FILTER_FIELDS = [
  "plant",
  "material",
  "approver",
  "fo_number",
  "owner",
  "po_creator",
  "segment",
  "mrs_non_mrs",
  "region",
];

const SECTIONS = [
  { id: "at-a-glance", label: "At a Glance" },
  { id: "pending-aging", label: "Pending Aging" },
  { id: "timeliness", label: "Timeliness Performance" },
  { id: "cost-count", label: "Cost & Count Analytics" },
  { id: "invalid-credit-note", label: "Invalid & Credit Notes" },
  { id: "rejection", label: "Rejection Overview" },
  { id: "e2e-timeliness", label: "E2E Timeliness" },
  { id: "material-spend-cost-spike", label: "Material Spend & Cost Spike" },
  { id: "cx-timeliness", label: "CX Timeliness" },
];

const THEME_KEY = "analytics-theme";

const getInitialTheme = () => {
  const stored = typeof window !== "undefined" && window.localStorage.getItem(THEME_KEY);
  if (stored === "light" || stored === "dark") return stored;
  const prefersDark = typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches;
  return prefersDark ? "dark" : "light";
};

const Analytics = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ trend: "ALL" });
  const [spikeFilters, setSpikeFilters] = useState(DEFAULT_SPIKE_FILTERS);
  const [refreshKey, setRefreshKey] = useState(0);
  const [theme, setTheme] = useState(getInitialTheme);
  const [isRefreshHovered, setIsRefreshHovered] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [layout, setLayout] = useState({ toolbarTop: 0, offset: 72 });
  const topbarRef = useRef(null);
  const toolbarRowRef = useRef(null);

  useEffect(() => {
    window.localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  // The top bar is only pinned on roomy screens (see the compact media query in
  // Analytics.css); the toolbar always pins, right below it when it is. Measure
  // both so scroll targets and the nav's active-section tracking clear them.
  useEffect(() => {
    const measure = () => {
      const topbar = topbarRef.current;
      const row = toolbarRowRef.current;
      if (!topbar || !row) return;
      const toolbarTop = getComputedStyle(topbar).position === "sticky" ? Math.round(topbar.getBoundingClientRect().height) : 0;
      setLayout({ toolbarTop, offset: toolbarTop + Math.round(row.getBoundingClientRect().height) });
    };
    measure();
    window.addEventListener("resize", measure);
    if (typeof ResizeObserver === "undefined") return () => window.removeEventListener("resize", measure);
    const observer = new ResizeObserver(measure);
    observer.observe(topbarRef.current);
    observer.observe(toolbarRowRef.current);
    return () => {
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
  }, []);

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => key !== "trend" && (Array.isArray(value) ? value.length > 0 : value)).length;
  const trendLabel = filters.day
    ? `${filters.year}-${filters.month}-${filters.day}`
    : filters.month
      ? `${filters.year}-${String(filters.month).padStart(2, "0")}`
      : filters.quarter
        ? `${filters.year} Q${filters.quarter}`
        : filters.year || TREND_OPTIONS.find((o) => o.value === filters.trend)?.label || "All Time";

  const handleJumpTo = useCallback((sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div
      className={`analytics-wrapper mds${filtersOpen ? " analytics-filters-open" : ""}`}
      data-theme={theme}
      style={{ "--an-sticky-offset": `${layout.offset}px`, "--an-toolbar-top": `${layout.toolbarTop}px` }}
    >
      <div className="analytics-topbar-wrap" ref={topbarRef}>
        <McTopBar product="Maersk | Unplanned Charges" productshort="Maersk" className="app-topbar analytics-topbar">
          <div slot="actions" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <LastRefreshed table="charge_details" />
            <ThemeToggle theme={theme} onToggle={() => setTheme((t) => (t === "dark" ? "light" : "dark"))} />
            <button onClick={() => navigate("/")} className="analytics-back-btn">
              Back to Home
            </button>
          </div>
        </McTopBar>
      </div>

      <div className="analytics-intro">
        <h3 className="analytics-main-header">Analytics Overview</h3>
        <p className="analytics-subheader">Real-time visibility into approvals, purchase orders, invoices, and cost across the unplanned charges pipeline.</p>
      </div>

      <div className="analytics-toolbar">
        <div className="analytics-toolbar-row" ref={toolbarRowRef}>
          <button
            type="button"
            className={`analytics-filters-toggle${filtersOpen ? " analytics-filters-toggle-open" : ""}`}
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-controls="analytics-filter-panel"
            title="Show or hide filters"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 5h18l-7 8v6l-4 2v-8z" />
            </svg>
            Filters
            {activeFilterCount > 0 && <span className="analytics-filters-badge">{activeFilterCount}</span>}
            <span className="analytics-filters-trend">{trendLabel}</span>
          </button>

          <SectionNav sections={SECTIONS} topOffset={layout.offset + 20} onNavigate={() => setFiltersOpen(false)} />

          <button
            type="button"
            className="analytics-refresh-btn"
            onClick={() => setRefreshKey((k) => k + 1)}
            onMouseEnter={() => setIsRefreshHovered(true)}
            onMouseLeave={() => setIsRefreshHovered(false)}
            title="Refresh all dashboards"
            aria-label="Refresh all dashboards"
          >
            <McIcon icon="rotate" size="16" color={isRefreshHovered ? "var(--an-accent)" : "var(--an-text)"} />
            <span className="analytics-refresh-label">Refresh</span>
          </button>
        </div>

        {/* Always mounted (just hidden) so draft filter values survive toggling. */}
        <div id="analytics-filter-panel" className="analytics-filter-panel" hidden={!filtersOpen}>
          <div className="analytics-filter-panel-header">
            <span className="analytics-filter-panel-title">Filters</span>
            <button
              type="button"
              className="analytics-filter-panel-close"
              onClick={() => setFiltersOpen(false)}
              aria-label="Close filters"
              title="Close filters"
            >
              <McIcon icon="times" size="16" color="var(--an-text)" />
            </button>
          </div>
          <FilterBar fields={FILTER_FIELDS} onApply={setFilters} onClearAll={() => setSpikeFilters(DEFAULT_SPIKE_FILTERS)} />
        </div>
      </div>

      <div className="analytics-container">
        <AtAGlanceSection id="at-a-glance" filters={filters} refreshKey={refreshKey} onJumpTo={handleJumpTo} />

        <AgingComparisonSection
          id="pending-aging"
          title="Pending Aging"
          metrics={[
            { id: "pending-approval", title: "Pending Approval Aging", fetcher: getPendingApprovalAging },
            { id: "pending-po", title: "Pending PO Aging", fetcher: getPendingPoAging },
          ]}
          filters={filters}
          refreshKey={refreshKey}
        />

        <TimelinessComparisonSection
          id="timeliness"
          title="Timeliness Performance"
          dashboards={[
            {
              id: "approval-timeliness",
              title: "Approval Timeliness",
              compareLabel: "Approval",
              fetcher: getApprovalTimeliness,
              actorLabel: "Approver",
              thresholdDays: 4,
              avgFieldPaths: ["avg_approval", "avg_approval_days"],
              oldestFieldPaths: null,
            },
            {
              id: "po-timeliness",
              title: "PO Timeliness",
              compareLabel: "PO",
              fetcher: getPoTimeliness,
              actorLabel: "PO Creator",
              thresholdDays: 3,
              avgFieldPaths: ["avg_po_creation", "avg_po_creation_days"],
              oldestFieldPaths: ["oldest_pending_days"],
            },
            {
              id: "invoice-timeliness",
              title: "Invoice Timeliness",
              compareLabel: "Invoice",
              fetcher: getInvoiceTimeliness,
              actorLabel: "Invoice Creator",
              labelFieldPaths: ["invoice_creator_name"],
              thresholdDays: 14,
              timeUnit: "Working Days",
              avgFieldPaths: ["avg_invoice_creation", "avg_invoice_creation_days"],
              oldestFieldPaths: ["oldest_pending_days"],
              variants: [
                { id: "po-to-invoice", label: "PO to Invoice" },
                {
                  id: "delivery-to-invoice",
                  label: "Delivery to Invoice",
                  metricPath: "delivery_to_invoice",
                  avgFieldPaths: ["avg_days"],
                  oldestFieldPaths: null,
                  thresholdDays: 28,
                },
              ],
            },
            {
              id: "awaiting-customer-timeliness",
              title: "Awaiting Customer",
              compareLabel: "Awaiting Customer",
              fetcher: getAwaitingTimeliness,
              actorLabel: "Approver",
              labelFieldPaths: ["approver_name"],
              matrixPaths: ["records"],
              chartMatrixPaths: ["approver_matrix"],
              chartLabelFieldPaths: ["approver_name"],
              chartRowValuePaths: ["avg_awaiting_hours"],
              avgFieldPaths: ["avg_awaiting_hours"],
              rowValuePaths: ["elapsed_hours"],
              oldestFieldPaths: null,
              valueDivisor: 24,
              thresholdDays: 1,
              timeUnit: "Days",
              timeSuffix: "d",
            },
          ]}
          filters={filters}
          refreshKey={refreshKey}
        />

        <CostCountSection id="cost-count" filters={filters} refreshKey={refreshKey} />

        <InvalidCreditNoteSection id="invalid-credit-note" filters={filters} refreshKey={refreshKey} />

        <RejectionSection id="rejection" filters={filters} refreshKey={refreshKey} />

        <E2ETimelinessSection id="e2e-timeliness" filters={filters} refreshKey={refreshKey} />

        <MaterialSpendCostSpikeSection
          id="material-spend-cost-spike"
          filters={filters}
          spikeFilters={spikeFilters}
          onSpikeFiltersChange={setSpikeFilters}
          refreshKey={refreshKey}
        />

        <CXTimelinessSection id="cx-timeliness" filters={filters} refreshKey={refreshKey} />
      </div>
    </div>
  );
};

export default Analytics;
